import React, { useRef, useEffect, useLayoutEffect, useState, Fragment } from 'react';

import * as THREE from 'three';
import { Grid, GizmoHelper, GizmoViewport, GizmoViewcube } from '@react-three/drei';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls, OrthographicCamera, PerspectiveCamera, Text, useHelper } from '@react-three/drei'
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import CssBaseline from '@material-ui/core/CssBaseline';
import { StatsGraph } from '@helpscout/stats'
import { ThemeProvider } from "@material-ui/core/styles";
import Typography from '@material-ui/core/Typography';

import SideBar from './pages/sidebar/sidebar';
import ObjectBk from './pages/objectBk/object';
import ObjectAk from './pages/objectAk/object';
import ObjectSoftInsert from './pages/objectSoftInsert/object';
import ObjectAfo from './pages/objectAfo/object';
import appStyle from './pages/style/appStyle';
import themeStyle from './pages/style/themeStyle';
import Param from './pages/store/param';
import Control from './pages/store/control';
import ShowCheckbox from './pages/sidebar/showcheckbox';
import { useControls, Leva } from 'leva'
import initOpenCascade from "opencascade.js";
import DraggableWindow from './components/DraggableWindow';


class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorInfo: '' };
  }
  
  static getDerivedStateFromError(error) { // Update state so the next render will show the fallback UI.
    return { hasError: true, errorInfo: error.toString() };
  }

  componentDidCatch(error, errorInfo) {
    console.log(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <ThemeProvider theme={themeStyle}>
          <div>
            <ShowCheckbox />
            <h1>Something went wrong. Sorry for the inconvenience.</h1>;
            <p>{this.state.errorInfo}</p>
            <p>Please send the screenshot to the person in charge.</p>
          </div> 
        </ThemeProvider>
      )
    }

    return this.props.children; 
  }
}

const Orbit = () => {
    const ref = useRef();
    const refLight = useRef();
    const { camera, gl } = useThree();
    const flgOrbitEnable = Control.Get('flgOrbitEnable');
    const cameraPosition = Control.Get('cameraPosition');
    const currentAngle = Control.Get('currentAngle');

    // useHelper(refLight, THREE.DirectionalLightHelper, 1, 'hotpink');

    useFrame(() => {
      if (refLight.current) {
        refLight.current.position.copy(camera.position);
      }
    });

    useEffect(() => {
        if (flgOrbitEnable) {
            //ref.current.enableZoom = true;
            ref.current.enableRotate = true;
            //ref.current.enablePan = true;
        }
        else {
            //ref.current.enableZoom = false;
            ref.current.enableRotate = false;
            //ref.current.enablePan = false;
        }
    }, [flgOrbitEnable]);


    useEffect(() => {
      if (ref.current) {
        if (currentAngle == 0) { // 0: Free
        } else {
          ref.current.target.set(0, 0, 0);
        }
        camera.position.set(...cameraPosition)
      }
    }, [cameraPosition, currentAngle]);

    return (
      <>
        <OrbitControls ref={ref} args={[camera, gl.domElement]} enableDamping={false} />
        <directionalLight 
          ref={refLight} 
          intensity={1} 
          position={[0, 0, 100]} 
          castShadow 
          shadow-mapSize-height={512} 
          shadow-mapSize-width={512} 
          shadow-camera-left={-400} 
          shadow-camera-right={400} 
          shadow-camera-top={400} 
          shadow-camera-bottom={-400} 
        />

        {/* <pointLight ref={refLight} color={'0xa09e5f'} intensity={1} position={[0, 0, 100]} /> */}

            {/*
      <GizmoHelper
        alignment="top-left" // widget alignment within scene
        margin={[50, 50]} // widget margins (X, Y)
      >
        <GizmoViewcube />
      </GizmoHelper>
      */}
      </>
    )
};

/*
// This is available only on perspective projection
const Instruction = () => {
  //const { camera } = useThree();
  const ref = useRef();

  const instructionText = Param.Get('instructionText');

  // TODO: set refresh interval
  useFrame(({ camera }) => {
    if (ref.current.position !== camera.position) {
      // Move mesh to be flush with camera
      ref.current.position.copy(camera.position);
      ref.current.quaternion.copy(camera.quaternion);

      // Apply offset
      ref.current.translateZ(-5);
      ref.current.translateY(-1.5);
    }
  });

  return (
    <Text 
      ref={ref}
      fontSize={0.1}
      color="#FFA500" // default
      anchorX="center" // default
      anchorY="middle" // default
    >
      {instructionText}
    </Text>
  );
};
*/



export const resetOc = () => {
  initOpenCascade().then(oc => {
      setParam(s => { s.oc = oc });
      toast.info('Finish initialization, Please start modeling.')
      //toast('Please select Below Knee or Above Knee, and Test Socket or Final Socket.');
  });
}

/**
 * The main component of the Instalimb Modeler frontend.
 * @returns {JSX.Element} The App component.
 */
const App = () => {
  const refLight = useRef();
  const refCamera = useRef()
  const style = appStyle();
  const levaRef = useRef()
  const [isInitOc, setIsInitOc] = useState(false);

  const prosthetics = Control.Get('prosthetics');
  const type = Control.Get('type');
  const orthographic = Control.Get('orthographic');
  const setParam = Param.Get('setParam');


  const color = useControls({
  ['Background Color']: '#242424',
  });

  /**
   * Sets the position and z-index of the Leva GUI.
   */
  useLayoutEffect(() => {
    if (levaRef.current) {
        levaRef.current.style.position = "absolute";
        levaRef.current.style.zIndex = 9999;
    }
  }, []);


  /**
   * Renders the ground grid.
   * @returns {JSX.Element} The Ground component.
   */
  function Ground() {
    const gridConfig = {
      cellSize: 100,
      cellThickness: 1,
      cellColor: '#f5f5f5',
      sectionSize: 10,
      sectionThickness: 0.5,
      sectionColor: '#dcdcdc',
      fadeDistance: 1000,
      fadeStrength: 1,
      followCamera: false,
      infiniteGrid: true,
      side: THREE.DoubleSide
    }
    return <Grid position={[0, 0, 0]} args={[1000, 1000]} {...gridConfig} />
  }

  /**
   * Initializes OpenCascade and sets the oc parameter in the setParam function.
   * @returns {null} Returns null.
   */
  function InitOpenCascade() { // setparam is only available inside the canvas
    const loadingToastId = toast.info('Start initializaion. Please wait for a while.', { autoClose: false });
    initOpenCascade().then(oc => {
        setParam(s => { s.oc = oc });
        toast.dismiss(loadingToastId); // Dismiss the toast
        toast.info('Finish initialization, Please start modeling.', {autoClose: 1000})
        //toast('Please select Below Knee or Above Knee, and Test Socket or Final Socket.');
        setIsInitOc(true);
    });

    return null;
  }


  return (
    <ErrorBoundary>
      <div className={style.root}> {/* base css */}
        <CssBaseline />
        {/* <StatsGraph />  */} {/* show fps, memory usage, etc */}
        <ThemeProvider theme={themeStyle}>
          <div className={style.ref}>
              <div className={style.themeRight}> {/* 3d object view, right side window */}
                <div ref={levaRef}>
                  <Leva
                  // hidden // default = false, when true the GUI is hidden
                  />
                </div>

                <Canvas camera={{ zoom: 2, far: 5000, near: 0.01 }} shadows >
                  <color attach='background' args={[color['Background Color']]} />
                  <PerspectiveCamera ref={refCamera} position={[0, 100, 500]} fov={90} makeDefault={!orthographic} />
                  <OrthographicCamera position={[0, 100, 500]} zoom={2} makeDefault={orthographic} />
                  {/* camera controller */}
                  <Orbit />
                  <Ground />
                  {/* <OrbitControls enableDamping={false}/> */}
                  {/* <gridHelper args={[500, 25]} /> */}
                  <axesHelper args={[200]} />
                  {(prosthetics == 'BK') && ((type == 'TestSocket') || (type == 'FinalDevice') || (type == 'FinalDeviceFinalStep')) ? <ObjectBk /> : null}
                  {(prosthetics == 'AK') && ((type == 'TestSocket') || (type == 'FinalDevice')) ? <ObjectAk /> : null}
                  {(prosthetics == 'LLO') && (type == 'AFO') ? <ObjectAfo /> : null}
                  {(type == 'SoftInsert') ? <ObjectSoftInsert /> : null}
                  {isInitOc === false && <InitOpenCascade />}
                  {/* <Instruction/> */}
                </Canvas>
              </div>
              <div className={style.themeLeft}> {/* user input view, left side window */}
                <SideBar /> {/* Left View */}
              </div>
          </div>
        </ThemeProvider>
        <DraggableWindow initialPosition={{ x: window.innerWidth * 0.3, y: window.innerHeight * 0.1 }} />
      </div>
    </ErrorBoundary>
  );
}
export default App;
